import { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Clock, MapPin, Loader2, Users, BarChart3, CheckCircle2, XCircle, AlertCircle, Building2 } from "lucide-react";
import { format, differenceInHours, differenceInMinutes } from "date-fns";

import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '../components/ui/form';
import { Badge } from '../components/ui/badge';

import { useToast } from '../hooks/use-toast';
import { useLanguage } from '../hooks/use-language';
import { apiRequest } from '../lib/queryClient';
import { useProjects } from '../hooks/use-projects';
import TopNav from '../components/layout/top-nav';
import Sidebar from '../components/layout/sidebar';
import MobileSidebar from '../components/layout/mobile-sidebar';

const timeclockFormSchema = z.object({
  employeeName: z.string().min(2, "Name must be at least 2 characters").refine(
    (val) => val !== "__manual__" && val !== "__loading__",
    "Please enter a worker name"
  ),
  projectId: z.string().min(1, "Project is required"),
  taskId: z.string().min(1, "Task is required"),
});

type TimeclockFormType = z.infer<typeof timeclockFormSchema>;

export default function TimeclockPage() {
  const { t } = useLanguage();
  const [activeTab, setActiveTab] = useState("clock");
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const { projects } = useProjects();
  const [currentLocation, setCurrentLocation] = useState<string>("");
  const [currentCoordinates, setCurrentCoordinates] = useState<{lat: number, lng: number} | null>(null);
  const [isLoadingLocation, setIsLoadingLocation] = useState(false);
  const [locationMatch, setLocationMatch] = useState<{match: boolean, distance?: number, projectAddress?: string} | null>(null);
  const [selectedProjectId, setSelectedProjectId] = useState<string>("");

  // Fetch tasks for selected project
  const { data: projectTasks = [] } = useQuery({
    queryKey: ['/api/tasks', selectedProjectId],
    queryFn: async () => {
      if (!selectedProjectId) return [];
      const response = await apiRequest("GET", `/api/tasks?projectId=${selectedProjectId}`);
      if (!response.ok) return [];
      const data = await response.json();
      return data.tasks || [];
    },
    enabled: !!selectedProjectId
  });

  // Get workers list
  const { data: workersData, error: workersError } = useQuery<{ workers: Array<{ id: number | null; name: string; email: string | null; phone: string | null; employeeId: string | null; role: string | null; hourlyRate: number | null; source: string }> }>({
    queryKey: ["/api/timeclock/workers"],
    refetchOnWindowFocus: false, // Don't refetch on tab switch to prevent logout
    retry: false,
    onError: (error: any) => {
      console.error('Error fetching workers:', error);
      // Don't log out on error, just show a toast
      if (error?.message?.includes('401') || error?.message?.includes('403')) {
        toast({
          title: "Authentication Error",
          description: "Please refresh the page and try again",
          variant: "destructive"
        });
      }
    }
  });
  const workers = workersData?.workers || [];
  
  const { data: user, error: userError } = useQuery<any>({
    queryKey: ["/api/user"],
    retry: false,
    onError: (error: any) => {
      console.error('Error fetching user:', error);
    }
  });

  // Get selected project details
  const selectedProject = projects.find((p: any) => p.id.toString() === selectedProjectId);
  const projectAddress = selectedProject?.client?.address 
    ? `${selectedProject.client.address}${selectedProject.client.city ? `, ${selectedProject.client.city}` : ''}${selectedProject.client.state ? `, ${selectedProject.client.state}` : ''}${selectedProject.client.zip ? ` ${selectedProject.client.zip}` : ''}`.trim()
    : null;

  // Get location and validate against project address
  const getLocation = async () => {
    if ("geolocation" in navigator) {
      setIsLoadingLocation(true);
      try {
        navigator.geolocation.getCurrentPosition(
          async (position) => {
            const { latitude, longitude } = position.coords;
            setCurrentCoordinates({ lat: latitude, lng: longitude });
            
            try {
              const response = await fetch(
                `https://nominatim.openstreetmap.org/reverse?format=json&lat=${latitude}&lon=${longitude}&zoom=18&addressdetails=1`
              );
              const data = await response.json();
              const locationName = data.display_name || `${latitude}, ${longitude}`;
              setCurrentLocation(locationName);
              
              // Validate location if project is selected
              if (selectedProjectId && projectAddress) {
                validateLocation(latitude, longitude, projectAddress);
              }
            } catch (error) {
              setCurrentLocation(`${latitude}, ${longitude}`);
            }
          },
          () => {
            setCurrentLocation("Location not available");
            setCurrentCoordinates(null);
          }
        );
      } catch (error) {
        setCurrentLocation("Location not available");
        setCurrentCoordinates(null);
      } finally {
        setIsLoadingLocation(false);
      }
    }
  };

  // Validate worker location against project address
  const validateLocation = async (lat: number, lng: number, projectAddr: string) => {
    try {
      // Geocode project address to get coordinates
      const projectResponse = await fetch(
        `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(projectAddr)}&limit=1`
      );
      const projectData = await projectResponse.json();
      
      if (projectData.length > 0) {
        const projectLat = parseFloat(projectData[0].lat);
        const projectLng = parseFloat(projectData[0].lon);
        
        // Calculate distance (Haversine formula)
        const R = 6371; // Earth radius in km
        const dLat = (projectLat - lat) * Math.PI / 180;
        const dLng = (projectLng - lng) * Math.PI / 180;
        const a = Math.sin(dLat/2) * Math.sin(dLat/2) +
                  Math.cos(lat * Math.PI / 180) * Math.cos(projectLat * Math.PI / 180) *
                  Math.sin(dLng/2) * Math.sin(dLng/2);
        const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
        const distance = R * c; // Distance in km
        
        // Convert to miles
        const distanceMiles = distance * 0.621371;
        
        // Consider it a match if within 0.1 miles (about 500 feet)
        setLocationMatch({
          match: distanceMiles <= 0.1,
          distance: distanceMiles,
          projectAddress: projectAddr
        });
    } else {
        setLocationMatch({ match: false, projectAddress: projectAddr });
      }
    } catch (error) {
      console.error('Location validation error:', error);
      setLocationMatch({ match: false, projectAddress: projectAddr });
    }
  };

  useEffect(() => {
    getLocation();
  }, []);

  // Re-validate when project changes
  useEffect(() => {
    if (selectedProjectId && currentCoordinates && projectAddress) {
      validateLocation(currentCoordinates.lat, currentCoordinates.lng, projectAddress);
    } else {
      setLocationMatch(null);
    }
  }, [selectedProjectId, projectAddress]);

  // Form setup
  const form = useForm<TimeclockFormType>({
    resolver: zodResolver(timeclockFormSchema),
    defaultValues: {
      employeeName: "",
      projectId: "",
      taskId: "",
    },
  });

  // Watch project selection
  const watchedProjectId = form.watch("projectId");
  useEffect(() => {
    setSelectedProjectId(watchedProjectId || "");
  }, [watchedProjectId]);

  // Get today's workers (active + completed)
  const { data: todayData, isLoading: loadingToday, error: todayError } = useQuery<{
    activeWorkers: any[];
    todayCompleted: any[];
  }>({
    queryKey: ["/api/timeclock/today"],
    refetchInterval: 300000, // Refresh every 5 minutes (reduced from 30 seconds to prevent logout issues)
    refetchOnWindowFocus: false, // Don't refetch on tab switch to prevent logout
    retry: false,
    onError: (error: any) => {
      console.error('Error fetching today\'s workers:', error);
      // Don't log out on error, just show a toast
      if (error?.message?.includes('401') || error?.message?.includes('403')) {
        toast({
          title: "Authentication Error",
          description: "Please refresh the page and try again",
          variant: "destructive"
        });
      }
    }
  });

  const activeWorkers = todayData?.activeWorkers || [];
  const todayCompleted = todayData?.todayCompleted || [];

  // Get hours report (only for business owners)
  const { data: hoursReportData, isLoading: loadingReport } = useQuery<any>({
    queryKey: ["/api/timeclock/report"],
    refetchOnWindowFocus: false, // Don't refetch on tab switch to prevent logout
    enabled: activeTab === "report" && !!user,
  });
  
  // Extract daily and weekly report from response
  const hoursReport = hoursReportData?.dailyReport || {};
  const weeklyReport = hoursReportData?.weeklyReport || {};

  // Find active clock-in for the current employee name
  const employeeName = form.watch("employeeName");
  const activeEntry = employeeName ? activeWorkers.find((entry: any) => {
    return entry.employee_name === employeeName && !entry.clock_out_time;
  }) : null;

  // Mutations for clock in and clock out registration
  const clockInMutation = useMutation({
    mutationFn: async (data: TimeclockFormType) => {
      const locationToUse = currentLocation || "Location not available";
      return await apiRequest("POST", "/api/timeclock/clock-in", {
        employeeName: data.employeeName,
        taskId: parseInt(data.taskId),
        projectId: parseInt(data.projectId),
        date: format(new Date(), "yyyy-MM-dd"),
        location: locationToUse,
      });
    },
    onSuccess: () => {
      toast({
        title: "Clocked In",
        description: "Successfully clocked in with location tracking",
        variant: "default"
      });
      // Reset form immediately so next person can clock in
      form.reset();
      setSelectedProjectId("");
      queryClient.invalidateQueries({ queryKey: ["/api/timeclock/today"] });
      queryClient.invalidateQueries({ queryKey: ["/api/timeclock/report"] });
      if (selectedProjectId) {
        queryClient.invalidateQueries({ queryKey: ["/api/timeclock/project", selectedProjectId] });
      }
    },
    onError: (error) => {
      toast({
        title: "Error Registering Clock In",
        description: error.message || "An error occurred while registering your clock in",
        variant: "destructive"
      });
    }
  });

  const clockOutMutation = useMutation({
    mutationFn: async (payload?: { id: number; projectId: number | null }) => {
      const entryId = payload?.id ?? activeEntry?.id;
      const projectId = payload?.projectId ?? activeEntry?.project_id ?? activeEntry?.projectId ?? null;
      if (!entryId) {
        throw new Error("No active clock-in found");
      }
      return await apiRequest("POST", "/api/timeclock/clock-out", {
        clockInEntryId: entryId,
        projectId,
      });
    },
    onSuccess: () => {
      toast({
        title: "Clocked Out",
        description: "Successfully clocked out",
        variant: "default"
      });
      form.reset();
      setSelectedProjectId("");
      queryClient.invalidateQueries({ queryKey: ["/api/timeclock/today"] });
      queryClient.invalidateQueries({ queryKey: ["/api/timeclock/report"] });
      if (selectedProjectId) {
        queryClient.invalidateQueries({ queryKey: ["/api/timeclock/project", selectedProjectId] });
      }
    },
    onError: (error) => {
      toast({
        title: "Error Registering Clock Out",
        description: error.message || "An error occurred while registering your clock out",
        variant: "destructive"
      });
    }
  });

  // Handle form submission
  const onSubmit = (data: TimeclockFormType) => {
    // Validate that employeeName is not a special value
    if (data.employeeName === "__manual__" || data.employeeName === "__loading__") {
      toast({
        title: "Invalid Worker",
        description: "Please select a worker or enter a name manually",
        variant: "destructive"
      });
      return;
    }

    // Check if the selected employee has an active entry
    const selectedEmployeeActiveEntry = activeWorkers.find((entry: any) => 
      entry.employee_name === data.employeeName && !entry.clock_out_time
    );
    
    if (selectedEmployeeActiveEntry) {
      const entryProjectId = selectedEmployeeActiveEntry.project_id ?? selectedEmployeeActiveEntry.projectId;
      if (entryProjectId != null && selectedProjectId !== String(entryProjectId)) {
        toast({
          title: "Wrong project",
          description: "Select the same project you clocked in on to clock out.",
          variant: "destructive",
        });
        return;
      }
      clockOutMutation.mutate({
        id: selectedEmployeeActiveEntry.id,
        projectId: entryProjectId ?? null,
      });
    } else {
      // Clock in new employee
      clockInMutation.mutate(data);
    }
  };

  // Calculate time worked for active entry
  const getTimeWorked = (timestamp: string) => {
    const startTime = new Date(timestamp);
    const now = new Date();
    const hours = differenceInHours(now, startTime);
    const minutes = differenceInMinutes(now, startTime) % 60;
    return { hours, minutes };
  };

  return (
    <div className="remodra-layout">
      <div className="hidden md:block">
        <Sidebar />
      </div>
      <MobileSidebar />
      <div className="remodra-main-mobile md:ml-64">
        <TopNav />
        <main className="dashboard-2025 bg-[var(--dash-bg)] min-h-screen p-6 md:p-8 pt-24 flex flex-col items-center">
          <div className="w-full space-y-6">
            <div>
              <h1 className="text-2xl font-bold text-[var(--dash-text)]">Time Clock</h1>
              <p className="text-sm text-[var(--dash-text-muted)] mt-0.5">Track work hours with location validation.</p>
            </div>

            <div className="flex gap-1 border-b border-[var(--dash-border)]">
              <button type="button" onClick={() => setActiveTab("clock")} className={`px-4 py-2.5 text-sm font-medium rounded-t-lg flex items-center gap-1 ${activeTab === "clock" ? "dash-tab-active" : "dash-tab-inactive hover:text-[var(--dash-text)]"}`}>
                <Clock className="h-4 w-4" />Clock
              </button>
              <button type="button" onClick={() => setActiveTab("workers")} className={`px-4 py-2.5 text-sm rounded-t-lg flex items-center gap-1 ${activeTab === "workers" ? "dash-tab-active" : "dash-tab-inactive hover:text-[var(--dash-text)]"}`}>
                <Users className="h-4 w-4" />Workers Today
              </button>
              <button type="button" onClick={() => setActiveTab("report")} className={`px-4 py-2.5 text-sm rounded-t-lg flex items-center gap-1 ${activeTab === "report" ? "dash-tab-active" : "dash-tab-inactive hover:text-[var(--dash-text)]"}`}>
                <BarChart3 className="h-4 w-4" />Report
              </button>
            </div>

            {activeTab === "clock" && (
            <div className="rounded-xl border border-[var(--dash-border)] bg-[var(--dash-card)] shadow-[var(--dash-shadow)] overflow-hidden">
              <div className="px-6 py-4 border-b border-[var(--dash-border)] flex items-center justify-between">
                <h2 className="text-lg font-semibold text-[var(--dash-text)]">Clock In/Out</h2>
                {activeEntry && (
                  <Badge variant="outline" className="dash-pill dash-pill-active">
                    <CheckCircle2 className="h-3 w-3 mr-1" />Clocked In
                  </Badge>
                )}
              </div>
              <div className="p-6 space-y-4">
                    <Form {...form}>
                      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
                      {/* Employee Name - Select from Workers */}
                        <FormField
                          control={form.control}
                          name="employeeName"
                          render={({ field }) => {
                            const isManualEntry = field.value && !workers.some(w => w.name === field.value);
                            return (
                              <FormItem>
                                <FormLabel className="text-sm text-[var(--dash-text)]">Select Worker</FormLabel>
                                <FormControl>
                                  {isManualEntry ? (
                                    <div className="space-y-2">
                                      <Input
                                        placeholder="Enter worker name"
                                        className="h-10"
                                        value={field.value}
                                        onChange={field.onChange}
                                        disabled={!!activeEntry}
                                      />
                                      <Button
                                        type="button"
                                        variant="ghost"
                                        size="sm"
                                        className="text-xs text-slate-400 hover:text-slate-300"
                                        onClick={() => {
                                          form.setValue("employeeName", "");
                                        }}
                                      >
                                        ← Select from list instead
                                      </Button>
                                    </div>
                                  ) : (
                                    <Select 
                                      onValueChange={field.onChange}
                                      value={field.value}
                                    >
                                      <SelectTrigger className="h-10">
                                        <SelectValue placeholder="Select a worker or enter name manually" />
                                      </SelectTrigger>
                                      <SelectContent>
                                        {workers.length === 0 ? (
                                          <SelectItem value="__loading__" disabled>
                                            Loading workers...
                                          </SelectItem>
                                        ) : (
                                          <>
                                            {workers.map((worker) => (
                                              <SelectItem key={worker.id || worker.name} value={worker.name}>
                                                <div className="flex flex-col">
                                                  <span>{worker.name}</span>
                                                  {worker.role && (
                                                    <span className="text-xs text-slate-400">{worker.role}</span>
                                                  )}
                                                </div>
                                              </SelectItem>
                                            ))}
                                            <SelectItem value="__manual__" className="text-slate-500 italic">
                                              + Enter name manually
                                            </SelectItem>
                                          </>
                                        )}
                                      </SelectContent>
                                    </Select>
                                  )}
                                </FormControl>
                                {field.value === "__manual__" && (
                                  <Input
                                    placeholder="Enter worker name"
                                    className="h-10 mt-2"
                                    onChange={(e) => {
                                      const value = e.target.value;
                                      form.setValue("employeeName", value || "__manual__");
                                    }}
                                    autoFocus
                                  />
                                )}
                                <FormMessage />
                                {workers.length === 0 && !isManualEntry && (
                                  <p className="text-xs text-slate-500 mt-1">
                                    No workers found. Add workers in the Agents section or select "Enter name manually".
                                  </p>
                                )}
                              </FormItem>
                            );
                          }}
                        />
                        
                      {/* Project Selection */}
                      <FormField
                        control={form.control}
                        name="projectId"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel className="text-sm text-[var(--dash-text)]">Project (Optional)</FormLabel>
                            <Select 
                              onValueChange={field.onChange} 
                              value={field.value}
                            >
                              <FormControl>
                                <SelectTrigger className="h-10">
                                  <SelectValue placeholder="Select project..." />
                                </SelectTrigger>
                              </FormControl>
                              <SelectContent>
                                {projects.map((project: any) => (
                                  <SelectItem key={project.id} value={project.id.toString()}>
                                    {project.title}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                            <FormMessage />
                          </FormItem>
                        )}
                      />

                      {/* Location Display & Validation */}
                      <div className="p-3 rounded-lg border border-[var(--dash-border)] bg-[var(--dash-bg)] space-y-2">
                        <div className="flex items-center gap-2 text-sm">
                          <MapPin className="h-4 w-4 text-[var(--dash-accent)]" />
                          <span className="font-medium text-[var(--dash-text)]">Current Location:</span>
                          {isLoadingLocation ? (
                            <Loader2 className="h-3 w-3 animate-spin text-[var(--dash-text-muted)]" />
                          ) : (
                            <span className="text-xs flex-1 truncate text-[var(--dash-text-muted)]">{currentLocation || "Getting location..."}</span>
                          )}
                        </div>
                        {selectedProject && projectAddress && (
                          <div className="pt-2 border-t border-[var(--dash-border)]">
                            <div className="flex items-center gap-2 text-sm mb-2">
                              <Building2 className="h-4 w-4 text-[var(--dash-accent)]" />
                              <span className="font-medium text-[var(--dash-text)]">Project Address:</span>
                              <span className="text-xs flex-1 truncate text-[var(--dash-text-muted)]">{projectAddress}</span>
                            </div>
                            {locationMatch && (
                              <div className={`flex items-center gap-2 text-xs p-2 rounded ${locationMatch.match ? 'bg-green-500/10 text-green-600 border border-green-500/30' : 'bg-amber-500/10 text-amber-600 border border-amber-500/30'}`}>
                                {locationMatch.match ? <><CheckCircle2 className="h-3 w-3" /><span>Location verified – within project area</span></> : <><AlertCircle className="h-3 w-3" /><span>{locationMatch.distance ? `Location mismatch – ${locationMatch.distance.toFixed(2)} miles from project` : 'Unable to verify location match'}</span></>}
                              </div>
                            )}
                          </div>
                        )}
                      </div>

                      {/* Task Selection */}
                        <FormField
                          control={form.control}
                          name="taskId"
                          render={({ field }) => (
                            <FormItem>
                            <FormLabel className="text-sm text-[var(--dash-text)]">Task *</FormLabel>
                              <FormControl>
                                <Select
                                  value={field.value}
                                  onValueChange={field.onChange}
                                  disabled={!selectedProjectId || projectTasks.length === 0}
                                >
                                  <SelectTrigger className="h-10">
                                    <SelectValue placeholder={
                                      !selectedProjectId 
                                        ? "Select a project first" 
                                        : projectTasks.length === 0 
                                        ? "No tasks available" 
                                        : "Select task..."
                                    } />
                                  </SelectTrigger>
                                  <SelectContent>
                                    {projectTasks.map((task: any) => (
                                      <SelectItem key={task.id} value={task.id.toString()}>
                                        {task.taskName}
                                      </SelectItem>
                                    ))}
                                  </SelectContent>
                                </Select>
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />

                      {/* Active Entry Display */}
                      {activeEntry && (
                        <div className="p-3 rounded-lg border border-[var(--dash-accent)]/30 bg-[var(--dash-accent)]/10">
                          <p className="text-sm font-medium text-[var(--dash-accent)]">Currently Clocked In</p>
                          {activeEntry.project_title && <p className="text-xs mt-0.5 flex items-center gap-1 text-[var(--dash-text)]"><Building2 className="h-3 w-3" />{activeEntry.project_title}</p>}
                          <p className="text-xs text-[var(--dash-text-muted)]">Started: {format(new Date(activeEntry.timestamp), "h:mm a")} • Duration: {getTimeWorked(activeEntry.timestamp).hours}h {getTimeWorked(activeEntry.timestamp).minutes}m</p>
                          {activeEntry.location && <p className="text-xs text-[var(--dash-text-muted)] mt-1 flex items-center gap-1 truncate"><MapPin className="h-3 w-3 flex-shrink-0" /><span className="truncate">{activeEntry.location}</span></p>}
                        </div>
                      )}

                      {/* Same-project required for clock out */}
                      {activeEntry && (() => {
                        const entryProjectId = activeEntry.project_id ?? activeEntry.projectId;
                        const entryProjectIdStr = entryProjectId != null ? String(entryProjectId) : '';
                        const isSameProject = entryProjectIdStr && selectedProjectId === entryProjectIdStr;
                        const mustSelectProject = entryProjectIdStr && !isSameProject;
                        return mustSelectProject ? (
                          <p className="text-xs text-amber-400 flex items-center gap-1">
                            To clock out, select the same project you clocked in on: &quot;{activeEntry.project_title || 'Project ' + entryProjectIdStr}&quot; above.
                          </p>
                        ) : null;
                      })()}

                      {/* Submit Button: disable Clock Out unless same project is selected */}
                        <Button
                          type="submit"
                        className="w-full h-11"
                          disabled={
                            clockInMutation.isPending || clockOutMutation.isPending ||
                            (activeEntry && (() => {
                              const entryProjectId = activeEntry.project_id ?? activeEntry.projectId;
                              if (entryProjectId == null) return false;
                              return selectedProjectId !== String(entryProjectId);
                            })())
                          }
                        >
                          {clockInMutation.isPending || clockOutMutation.isPending ? (
                            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        ) : activeEntry ? (
                          <>
                            <XCircle className="h-4 w-4 mr-2" />
                            Clock Out
                          </>
                          ) : (
                          <>
                            <CheckCircle2 className="h-4 w-4 mr-2" />
                            Clock In
                          </>
                          )}
                        </Button>
                      </form>
                    </Form>
              </div>
            </div>
            )}

            {activeTab === "workers" && (
            <div className="space-y-4">
              <div className="rounded-xl border border-[var(--dash-border)] bg-[var(--dash-card)] shadow-[var(--dash-shadow)] overflow-hidden">
                <div className="px-6 py-4 border-b border-[var(--dash-border)]">
                  <h2 className="text-lg font-semibold text-[var(--dash-text)] flex items-center gap-2">
                    <CheckCircle2 className="h-5 w-5" />Active Workers ({activeWorkers.length})
                  </h2>
                </div>
                <div className="p-6">
                  {loadingToday ? (
                    <div className="flex items-center justify-center py-8 text-[var(--dash-text-muted)]">
                      <Loader2 className="h-6 w-6 animate-spin text-[var(--dash-accent)]" />
                    </div>
                  ) : activeWorkers.length === 0 ? (
                    <div className="text-center py-8">
                      <Users className="h-12 w-12 mx-auto mb-3 text-[var(--dash-text-muted)] opacity-50" />
                      <p className="text-sm text-[var(--dash-text)]">No active workers</p>
                      <p className="text-xs mt-1 text-[var(--dash-text-muted)]">Workers will appear here when they clock in</p>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      {activeWorkers.map((entry: any) => {
                        const clockInMs = entry.clock_in_time || (entry.timestamp ? new Date(entry.timestamp).getTime() : null);
                        const timeWorked = clockInMs ? getTimeWorked(new Date(clockInMs).toISOString()) : { hours: 0, minutes: 0 };
                        return (
                          <div key={entry.id} className="p-3 rounded-lg border border-[var(--dash-border)] bg-[var(--dash-bg)]">
                            <div className="flex items-start justify-between gap-3">
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2 mb-1">
                                  <span className="font-medium text-sm text-[var(--dash-text)]">{entry.employee_name}</span>
                                  <Badge variant="default" className="text-xs dash-pill dash-pill-active">Clocked In</Badge>
                                </div>
                                <div className="text-xs text-[var(--dash-text-muted)] space-y-0.5 mt-2">
                                  {entry.project_title && <p className="flex items-center gap-1"><Building2 className="h-3 w-3" />{entry.project_title}</p>}
                                  <p className="flex items-center gap-1"><Clock className="h-3 w-3" />{timeWorked.hours}h {timeWorked.minutes}m • Started: {clockInMs ? format(new Date(clockInMs), "h:mm a") : 'N/A'}</p>
                                  {entry.location && <p className="flex items-start gap-1 truncate"><MapPin className="h-3 w-3 mt-0.5 flex-shrink-0" /><span className="truncate">{entry.location}</span></p>}
                                </div>
                              </div>
                              <Button size="sm" variant="destructive" onClick={() => clockOutMutation.mutate({ id: entry.id, projectId: entry.project_id ?? entry.projectId ?? null })} disabled={clockOutMutation.isPending} className="flex-shrink-0">
                                {clockOutMutation.isPending ? <Loader2 className="h-3 w-3 animate-spin" /> : <><XCircle className="h-3 w-3 mr-1" />Clock Out</>}
                              </Button>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              </div>

              <div className="rounded-xl border border-[var(--dash-border)] bg-[var(--dash-card)] shadow-[var(--dash-shadow)] overflow-hidden">
                <div className="px-6 py-4 border-b border-[var(--dash-border)]">
                  <h2 className="text-lg font-semibold text-[var(--dash-text)]">Today's Completed ({todayCompleted.length})</h2>
                </div>
                <div className="p-6">
                  {loadingToday ? (
                    <div className="flex items-center justify-center py-6"><Loader2 className="h-5 w-5 animate-spin text-[var(--dash-accent)]" /></div>
                  ) : todayCompleted.length === 0 ? (
                    <p className="text-sm text-[var(--dash-text-muted)] text-center py-4">No completed entries today</p>
                  ) : (
                    <div className="space-y-2">
                      {todayCompleted.map((entry: any) => {
                        const clockInMs = entry.clock_in_time || (entry.timestamp ? new Date(entry.timestamp).getTime() : null);
                        const clockOutMs = entry.clock_out_time;
                        return (
                          <div key={entry.id} className="flex items-center justify-between p-3 rounded-lg border border-[var(--dash-border)] bg-[var(--dash-bg)]">
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2 mb-1">
                                <span className="font-medium text-sm text-[var(--dash-text)]">{entry.employee_name}</span>
                                <Badge variant="outline" className="text-xs dash-pill">Completed</Badge>
                              </div>
                              <div className="text-xs text-[var(--dash-text-muted)] space-y-0.5">
                                {entry.project_title && <p className="flex items-center gap-1"><Building2 className="h-3 w-3" />{entry.project_title}</p>}
                                <p>{clockInMs ? format(new Date(clockInMs), "h:mm a") : 'N/A'} – {clockOutMs ? format(new Date(clockOutMs), "h:mm a") : 'N/A'}</p>
                                {entry.location && <p className="flex items-center gap-1 truncate"><MapPin className="h-3 w-3 flex-shrink-0" /><span className="truncate">{entry.location}</span></p>}
                              </div>
                            </div>
                            <div className="text-right ml-4">{entry.hours_worked && <span className="font-bold text-sm text-[var(--dash-accent)]">{parseFloat(entry.hours_worked).toFixed(1)}h</span>}</div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              </div>
            </div>
            )}

            {activeTab === "report" && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="rounded-xl border border-[var(--dash-border)] bg-[var(--dash-card)] shadow-[var(--dash-shadow)] overflow-hidden">
                <div className="px-6 py-4 border-b border-[var(--dash-border)]">
                  <h2 className="text-base font-semibold text-[var(--dash-text)]">Daily Report</h2>
                </div>
                <div className="p-6">
                {loadingReport ? (
                  <div className="flex items-center justify-center py-6"><Loader2 className="h-5 w-5 animate-spin text-[var(--dash-accent)]" /></div>
                ) : Object.keys(hoursReport).length === 0 ? (
                  <p className="text-sm text-[var(--dash-text-muted)] text-center py-4">No data available</p>
                ) : (
                  <div className="space-y-2">
                    {Object.entries(hoursReport).map(([employee, hours]: [string, any]) => (
                      <div key={employee} className="flex justify-between items-center p-2 rounded border border-[var(--dash-border)] bg-[var(--dash-bg)]">
                        <span className="text-sm text-[var(--dash-text)] truncate">{employee}</span>
                        <span className="font-bold text-sm text-[var(--dash-accent)] ml-2">{hours}h</span>
                      </div>
                    ))}
                  </div>
                )}
                </div>
              </div>
              <div className="rounded-xl border border-[var(--dash-border)] bg-[var(--dash-card)] shadow-[var(--dash-shadow)] overflow-hidden">
                <div className="px-6 py-4 border-b border-[var(--dash-border)]">
                  <h2 className="text-base font-semibold text-[var(--dash-text)]">Weekly Report</h2>
                </div>
                <div className="p-6">
                  {loadingReport ? (
                    <div className="flex items-center justify-center py-6"><Loader2 className="h-5 w-5 animate-spin text-[var(--dash-accent)]" /></div>
                  ) : Object.keys(weeklyReport).length === 0 ? (
                    <p className="text-sm text-[var(--dash-text-muted)] text-center py-4">No data available</p>
                  ) : (
                    <div className="space-y-2">
                      {Object.entries(weeklyReport).map(([employee, hours]: [string, any]) => (
                        <div key={employee} className="flex justify-between items-center p-2 rounded border border-[var(--dash-border)] bg-[var(--dash-bg)]">
                          <span className="text-sm text-[var(--dash-text)] truncate">{employee}</span>
                          <span className="font-bold text-sm text-[var(--dash-accent)] ml-2">{hours}h</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>
            )}
          </div>
        </main>
      </div>
    </div>
  );
}